import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Search, MapPin, CheckCircle, XCircle } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

const PostcodeChecker = () => {
  const [postcode, setPostcode] = useState("");
  const [isChecking, setIsChecking] = useState(false);
  const [showResult, setShowResult] = useState(false);
  const [isCovered, setIsCovered] = useState(false);
  const { toast } = useToast();

  const handleCheck = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!postcode.trim()) {
      toast({
        title: "Please enter a postcode",
        description: "Enter your full UK postcode to check coverage",
        variant: "destructive",
      });
      return;
    }

    setIsChecking(true);

    try {
      const { data, error } = await supabase.functions.invoke('check-postcode-coverage', {
        body: { postcode: postcode.trim().toUpperCase() },
      });

      if (error) throw error;

      setIsCovered(data.covered);
      setShowResult(true);
    } catch (error) {
      console.error("Error checking postcode:", error);
      toast({
        title: "Error checking postcode",
        description: "Please check your postcode and try again",
        variant: "destructive",
      });
    } finally {
      setIsChecking(false);
    }
  };

  return (
    <>
      <section className="py-16 bg-gradient-to-br from-primary/5 via-background to-primary/10">
        <div className="container mx-auto px-4">
          <div className="max-w-2xl mx-auto text-center">
            <div className="flex items-center justify-center gap-2 mb-4">
              <MapPin className="w-6 h-6 text-primary" />
              <h2 className="text-3xl md:text-4xl font-bold text-foreground">
                Check We Cover Your Location
              </h2>
            </div>
            <p className="text-lg text-muted-foreground mb-8">
              Enter your postcode to see if we service your area in Berkshire, Hampshire & Surrey
            </p>
            
            <form onSubmit={handleCheck} className="flex gap-3 max-w-md mx-auto">
              <Input
                type="text"
                placeholder="Enter your postcode (e.g. RG1 1AA)"
                value={postcode}
                onChange={(e) => setPostcode(e.target.value)}
                className="text-lg h-12"
                disabled={isChecking}
              />
              <Button 
                type="submit" 
                size="lg" 
                className="gap-2"
                disabled={isChecking}
              >
                <Search className="w-5 h-5" />
                {isChecking ? "Checking..." : "Check"}
              </Button>
            </form>
          </div>
        </div>
      </section>

      <Dialog open={showResult} onOpenChange={setShowResult}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <div className="flex items-center justify-center mb-4">
              {isCovered ? (
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center">
                  <CheckCircle className="w-10 h-10 text-green-600" />
                </div>
              ) : (
                <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center">
                  <XCircle className="w-10 h-10 text-red-600" />
                </div>
              )}
            </div>
            <DialogTitle className="text-center text-2xl">
              {isCovered ? "Great News!" : "Sorry"}
            </DialogTitle>
            <DialogDescription className="text-center text-lg">
              {isCovered ? (
                <div className="space-y-4">
                  <p className="text-foreground font-semibold">
                    We cover your area!
                  </p>
                  <p>
                    Postcode <span className="font-bold">{postcode.toUpperCase()}</span> is within our service area.
                  </p>
                  <div className="pt-4 space-y-3">
                    <Button className="w-full" size="lg" asChild>
                      <a href="tel:07836393815">
                        Call Now: 07836 393815
                      </a>
                    </Button>
                    <Button variant="outline" className="w-full" size="lg" asChild>
                      <a href="/contact">
                        Get Free Quote
                      </a>
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="space-y-4">
                  <p className="text-foreground font-semibold">
                    We don't cover this area
                  </p>
                  <p>
                    Unfortunately, postcode <span className="font-bold">{postcode.toUpperCase()}</span> is outside our current service area.
                  </p>
                  <p className="text-sm">
                    We currently serve Berkshire, Hampshire and Surrey only.
                  </p>
                </div>
              )}
            </DialogDescription>
          </DialogHeader>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default PostcodeChecker;
